/**
 * NotificationsPage — Centro Notifiche
 * Il Diario di Bordo - Frontend
 *
 * Hub self-service: Feed + Preferenze + Non disturbare + Dispositivi
 */

import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Bell, BellOff, Check, X, Smartphone, Monitor, Trash2,
  AlertCircle, AlertTriangle, Info, MessageSquare, Calendar, CreditCard, Shield, AtSign, Heart
} from 'lucide-react'
import { notificationsApi, notificationPreferencesApi, pushApi } from '../services/api'
import { useNotificationFeed } from '../hooks/useNotificationFeed'
import { usePushNotifications } from '../hooks/usePushNotifications'
import { useAuth } from '../contexts/AuthContext'
import MessaggiPanel from '../components/comunicazioni/MessaggiPanel'
import PromemoriaPanel from '../components/comunicazioni/PromemoriaPanel'

// Tab dell'hub Centro Comunicazioni. Il valore "key" mappa direttamente
// al query param ?tab=<key>.
const TABS = [
  { key: 'feed', label: 'Notifiche' },
  { key: 'messaggi', label: 'Messaggi' },
  { key: 'promemoria', label: 'Promemoria' },
  { key: 'impostazioni', label: 'Impostazioni' }
]

const CATEGORY_META = {
  urgentAlerts: { label: 'Segnalazioni urgenti', icon: AlertCircle, color: 'text-red-500' },
  smartAlerts: { label: 'Smart Alert dati minori', icon: AlertTriangle, color: 'text-amber-500' },
  pep: { label: 'PEP', icon: Info, color: 'text-purple-500' },
  messages: { label: 'Nuovi messaggi', icon: MessageSquare, color: 'text-blue-500' },
  mentions: { label: 'Menzioni @user', icon: AtSign, color: 'text-indigo-500' },
  reactions: { label: 'Reazioni emoji', icon: Heart, color: 'text-pink-500' },
  reminders: { label: 'Promemoria', icon: Calendar, color: 'text-emerald-500' },
  billing: { label: 'Billing/abbonamento', icon: CreditCard, color: 'text-orange-500' },
  security: { label: 'Sicurezza account', icon: Shield, color: 'text-gray-600' }
}

const TYPE_ICON = {
  segnalazione_urgente: AlertCircle,
  alert: AlertTriangle,
  message: MessageSquare,
  mention: AtSign,
  reaction: Heart,
  reminder: Calendar,
  billing: CreditCard,
  security: Shield,
  system: Info,
  pep: Info
}

export default function NotificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { unreadCount } = useNotificationFeed()

  // ?tab=feed|messaggi|promemoria|impostazioni · default feed
  const rawTab = searchParams.get('tab') || 'feed'
  const activeTab = TABS.some((t) => t.key === rawTab) ? rawTab : 'feed'
  // ?thread=<id> usato solo dalla tab Messaggi
  const activeThreadId = searchParams.get('thread') || null

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', key)
    // Cambiando tab pulisco il thread (la tab Messaggi lo reimposterà se serve)
    next.delete('thread')
    setSearchParams(next)
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  const setThread = (id) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'messaggi')
    if (id) next.set('thread', id)
    else next.delete('thread')
    setSearchParams(next)
  }

  // La tab Messaggi necessita altezza piena (composer fisso in basso, anche
  // con tastiera virtuale aperta su mobile). Le altre tab usano scroll
  // normale. 100dvh - 56px header mobile - 64px bottom nav mobile. Desktop
  // (lg+): full viewport. -mb-20 lg:mb-0 cancella il pb-20 del main su
  // mobile (vedi AppLayout) per non sommare due volte la bottom nav.
  const isFullHeightTab = activeTab === 'messaggi'

  return (
    <div className={
      isFullHeightTab
        ? 'flex flex-col bg-gray-50 dark:bg-gray-900 h-[calc(100dvh-56px-64px)] lg:h-screen -mb-20 lg:mb-0'
        : ''
    }>
      {/* Header hub + tab bar: sticky per le tab "normali" (scroll a body),
          flex-shrink-0 per la tab Messaggi (layout app-shell full-height) */}
      <div className={`bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 ${
        isFullHeightTab ? 'flex-shrink-0' : 'sticky top-14 lg:top-0 z-20'
      }`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6 pb-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Bell className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
            Centro Comunicazioni
            {unreadCount > 0 && (
              <span className="ml-2 min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </h1>
          <nav className="flex gap-1 overflow-x-auto overflow-y-hidden mt-4" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setTab(tab.key)}
                className={`whitespace-nowrap px-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content area: tab "normali" scrollano col body (nessun container scrollabile
          intermedio → no scrollbar fantasma full-width); Messaggi resta full-height */}
      <div className={isFullHeightTab ? 'flex-1 min-h-0' : ''}>
        {activeTab === 'feed' && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
            <FeedTab navigate={navigate} />
          </div>
        )}
        {activeTab === 'messaggi' && (
          <MessaggiPanel threadId={activeThreadId} onSelectThread={setThread} />
        )}
        {activeTab === 'promemoria' && (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
            <PromemoriaPanel />
          </div>
        )}
        {activeTab === 'impostazioni' && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-10">
            <PreferencesSection title="Canali" description="Scegli per quali tipologie ricevere notifiche, e su quale canale.">
              <PreferencesTab />
            </PreferencesSection>

            <PreferencesSection title="Non disturbare" description="Imposta una fascia oraria in cui ricevere solo le notifiche urgenti.">
              <QuietHoursTab />
            </PreferencesSection>

            <PreferencesSection title="Dispositivi" description="Gestisci i dispositivi che ricevono le notifiche push.">
              <DevicesTab />
            </PreferencesSection>
          </div>
        )}
      </div>
    </div>
  )
}

function PreferencesSection({ title, description, children }) {
  return (
    <section>
      <div className="flex items-baseline justify-between border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
      </div>
      {description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{description}</p>
      )}
      {children}
    </section>
  )
}

// ────────────────────────────────────────────────────────────
// FEED TAB
// ────────────────────────────────────────────────────────────
function FeedTab({ navigate }) {
  const [filter, setFilter] = useState({})
  // Feed condiviso via Provider (no doppia fetch con NotificationBell)
  const { items: allItems, unreadCount, loading, hasMore, loadMore, markRead, dismiss, markAllRead } =
    useNotificationFeed()

  // Filtri applicati client-side sull'array già caricato
  const filteredItems = allItems.filter((n) => {
    if (filter.unreadOnly && n.read) return false
    if (filter.type && n.type !== filter.type) return false
    if (filter.severity && n.severity !== filter.severity) return false
    return true
  })

  // Raggruppamento notifiche chat per threadId.
  // Notifiche di tipo message/mention con stesso payload.threadId vengono
  // collassate in una sola riga "N nuovi messaggi da {sender}" (o gruppo).
  // Le altre notifiche restano singole.
  const items = (() => {
    const result = []
    const groupsByThread = new Map() // threadId → index in result
    for (const n of filteredItems) {
      const isChatLike = (n.type === 'message' || n.type === 'mention') && n.payload?.threadId
      if (!isChatLike) {
        result.push({ kind: 'single', n })
        continue
      }
      const tid = String(n.payload.threadId)
      const existingIdx = groupsByThread.get(tid)
      if (existingIdx === undefined) {
        // Nuovo gruppo: rappresentante = questa notifica
        const group = {
          kind: 'group',
          threadId: tid,
          threadType: n.payload?.threadType,
          threadName: n.payload?.threadName,
          // collezione di "chi ha scritto" per il summary
          senders: new Map(),
          count: 0,
          unread: 0,
          mentions: 0,
          mostRecent: n,
          items: []
        }
        groupsByThread.set(tid, result.length)
        result.push(group)
      }
      const grp = result[groupsByThread.get(tid)]
      grp.count++
      if (!n.read) grp.unread++
      if (n.type === 'mention') grp.mentions++
      const senderName = n.payload?.senderName || 'Utente'
      grp.senders.set(senderName, (grp.senders.get(senderName) || 0) + 1)
      // Tieni il più recente come "rappresentante" per createdAt
      if (new Date(n.createdAt) > new Date(grp.mostRecent.createdAt)) grp.mostRecent = n
      grp.items.push(n)
    }
    return result
  })()

  const handleClick = (n) => {
    if (!n.read) markRead(n._id)
    if (!n.actionUrl) return
    // SPA navigation solo se same-origin (defense-in-depth contro open redirect)
    try {
      const u = new URL(n.actionUrl, window.location.origin)
      if (u.origin === window.location.origin) {
        navigate(u.pathname + u.search)
      }
      // se cross-origin: ignora (gli actionUrl validi sono sempre path interni)
    } catch {
      // URL malformato: ignora
    }
  }

  const handleGroupClick = (group) => {
    // Segna tutti gli items del gruppo come letti, poi naviga alla chat
    group.items.forEach((n) => { if (!n.read) markRead(n._id) })
    const u = `/messaggi/${group.threadId}`
    navigate(u)
  }

  // Etichetta sintetica del gruppo. Mostra il numero di messaggi NUOVI
  // (unread). Se tutti già letti, mostra il count totale come storico.
  const groupTitle = (g) => {
    const n = g.unread > 0 ? g.unread : g.count
    // Filtra "Utente" (fallback per vecchie notifiche pre-payload-senderName)
    // se ci sono nomi reali nella lista.
    const allSenders = Array.from(g.senders.entries())
    const realSenders = allSenders.filter(([name]) => name !== 'Utente')
    const senderEntries = realSenders.length > 0 ? realSenders : allSenders

    if (g.threadType === 'channel' && g.threadName) {
      return g.unread > 0
        ? `${n} nuov${n === 1 ? 'o messaggio' : 'i messaggi'} in ${g.threadName}`
        : `${g.threadName} · ${n} messagg${n === 1 ? 'io' : 'i'}`
    }
    if (senderEntries.length === 1) {
      return g.unread > 0
        ? `${n} nuov${n === 1 ? 'o messaggio' : 'i messaggi'} da ${senderEntries[0][0]}`
        : `${senderEntries[0][0]} · ${n} messagg${n === 1 ? 'io' : 'i'}`
    }
    if (senderEntries.length === 2) {
      return g.unread > 0
        ? `${n} nuovi messaggi da ${senderEntries[0][0]} e ${senderEntries[1][0]}`
        : `${senderEntries[0][0]} e ${senderEntries[1][0]} · ${n} messaggi`
    }
    return g.unread > 0
      ? `${n} nuovi messaggi da ${senderEntries.length} persone`
      : `${senderEntries.length} persone · ${n} messaggi`
  }

  const formatTime = (iso) => {
    const d = new Date(iso)
    const diff = Date.now() - d.getTime()
    if (diff < 60_000) return 'ora'
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m fa`
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h fa`
    return d.toLocaleDateString('it-IT')
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilter({})}
            className={`px-3 py-1 text-xs rounded-full ${!filter.unreadOnly && !filter.type
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
          >
            Tutte
          </button>
          <button
            onClick={() => setFilter({ unreadOnly: true })}
            className={`px-3 py-1 text-xs rounded-full ${filter.unreadOnly
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
          >
            Non lette ({unreadCount})
          </button>
          {['segnalazione_urgente', 'alert', 'message', 'mention', 'reminder', 'billing'].map((t) => (
            <button
              key={t}
              onClick={() => setFilter({ type: t })}
              className={`px-3 py-1 text-xs rounded-full ${filter.type === t
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
            >
              {t.replace('_', ' ')}
            </button>
          ))}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-xs text-primary-600 hover:underline"
          >
            Segna tutte come lette
          </button>
        )}
      </div>

      {items.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Bell className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p>Nessuna notifica</p>
        </div>
      )}

      <ul className="space-y-2">
        {items.map((item) => {
          // Item gruppo (più messaggi/mention dello stesso thread aggregati)
          if (item.kind === 'group') {
            const g = item
            const Icon = g.mentions > 0 ? AtSign : MessageSquare
            const iconColor = g.mentions > 0 ? 'text-indigo-500' : 'text-blue-500'
            return (
              <li
                key={`grp:${g.threadId}`}
                className={`group p-4 rounded-lg border transition-colors cursor-pointer ${
                  g.unread === 0
                    ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
                }`}
                onClick={() => handleGroupClick(g)}
              >
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    <Icon className={`h-5 w-5 mt-0.5 ${iconColor}`} />
                    {g.unread > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {g.unread > 9 ? '9+' : g.unread}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`font-medium text-sm ${g.unread === 0 ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
                        {groupTitle(g)}
                      </p>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {formatTime(g.mostRecent.createdAt)}
                      </span>
                    </div>
                    {g.mentions > 0 && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                        ⚠ {g.mentions} menzion{g.mentions === 1 ? 'e' : 'i'} a te
                      </p>
                    )}
                  </div>
                </div>
              </li>
            )
          }
          // Item singolo (notifica non-chat o altro)
          const n = item.n
          const Icon = TYPE_ICON[n.type] || Info
          return (
            <li
              key={n._id}
              className={`group p-4 rounded-lg border transition-colors cursor-pointer ${
                n.read
                  ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
              }`}
              onClick={() => handleClick(n)}
            >
              <div className="flex items-start gap-3">
                <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                  n.severity === 'critical' ? 'text-red-500' :
                  n.severity === 'warning' ? 'text-amber-500' :
                  'text-blue-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`font-medium text-sm ${n.read ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
                      {n.title}
                    </p>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {formatTime(n.createdAt)}
                    </span>
                  </div>
                  {n.body && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{n.body}</p>
                  )}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); dismiss(n._id) }}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"
                  aria-label="Rimuovi"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </li>
          )
        })}
      </ul>

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loading}
          className="mt-4 mx-auto block px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded"
        >
          {loading ? 'Caricamento...' : 'Carica altre'}
        </button>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// PREFERENCES TAB
// ────────────────────────────────────────────────────────────
function PreferencesTab() {
  const { isEducatore } = useAuth()
  const [prefs, setPrefs] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Categorie visibili in base al ruolo: educatore non ha visibilità billing
  const visibleCategories = Object.entries(CATEGORY_META).filter(([key]) => {
    if (isEducatore() && key === 'billing') return false
    return true
  })

  const load = useCallback(async () => {
    try {
      const { data } = await notificationPreferencesApi.get()
      setPrefs(data?.data || {})
    } catch (e) {
      setError(e.message)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const toggle = async (category, channel) => {
    if (!prefs?.channels) return
    const next = !prefs.channels[category]?.[channel]
    setPrefs((p) => ({
      ...p,
      channels: { ...p.channels, [category]: { ...p.channels[category], [channel]: next } }
    }))
    setSaving(true)
    try {
      await notificationPreferencesApi.patch({
        channels: { [category]: { [channel]: next } }
      })
    } catch (e) {
      setError(e.message)
      load()
    } finally {
      setSaving(false)
    }
  }

  const toggleMaster = async (channel) => {
    if (!prefs) return
    const next = !prefs[channel]
    setPrefs((p) => ({ ...p, [channel]: next }))
    setSaving(true)
    try {
      await notificationPreferencesApi.patch({ [channel]: next })
    } catch (e) {
      load()
    } finally {
      setSaving(false)
    }
  }

  if (!prefs) return <p className="text-gray-500">Caricamento...</p>

  const masterPushOff = prefs.push === false
  const masterEmailOff = prefs.email === false
  const masterInAppOff = prefs.inApp === false
  const anyMasterOff = masterPushOff || masterEmailOff || masterInAppOff

  // Costruisce un testo descrittivo della riga master in base allo stato
  const masterDescription = () => {
    if (!anyMasterOff) {
      return 'Tutte le tipologie di notifica sono abilitate. Disattiva un canale per silenziarle tutte.'
    }
    const off = []
    if (masterInAppOff) off.push('in app')
    if (masterPushOff) off.push('push')
    if (masterEmailOff) off.push('email')
    const list = off.join(' / ')
    return `⚠ Notifiche ${list} disattivate per tutte le categorie. Riattiva il toggle per ricevere notifiche.`
  }

  return (
    <div>
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="grid grid-cols-[1fr,52px,52px,52px] sm:grid-cols-[1fr,72px,72px,72px] gap-2 px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-800/50 text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
          <div>Categoria</div>
          <div className="text-center">In app</div>
          <div className="text-center">Push</div>
          <div className="text-center">Email</div>
        </div>

        {/* Riga master: include descrizione inline che cambia in base agli switch */}
        <div className="grid grid-cols-[1fr,52px,52px,52px] sm:grid-cols-[1fr,72px,72px,72px] gap-2 px-3 sm:px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 items-center">
          <div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">Tutte le notifiche</div>
            <div className={`text-xs mt-0.5 ${anyMasterOff ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
              {masterDescription()}
            </div>
          </div>
          <ToggleCell on={prefs.inApp} onChange={() => toggleMaster('inApp')} disabled={saving} />
          <ToggleCell on={prefs.push} onChange={() => toggleMaster('push')} disabled={saving} />
          <ToggleCell on={prefs.email} onChange={() => toggleMaster('email')} disabled={saving} />
        </div>

        {visibleCategories.map(([key, meta]) => {
          const Icon = meta.icon
          const channels = prefs.channels?.[key] || {}
          return (
            <div
              key={key}
              className="grid grid-cols-[1fr,52px,52px,52px] sm:grid-cols-[1fr,72px,72px,72px] gap-2 px-3 sm:px-4 py-3 border-b last:border-b-0 border-gray-200 dark:border-gray-700 items-center"
            >
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 min-w-0">
                <Icon className={`h-4 w-4 flex-shrink-0 ${meta.color}`} />
                <span className="truncate">{meta.label}</span>
              </div>
              <ToggleCell on={channels.inApp} onChange={() => toggle(key, 'inApp')} disabled={saving} />
              <ToggleCell on={channels.push} onChange={() => toggle(key, 'push')} disabled={saving} />
              <ToggleCell on={channels.email} onChange={() => toggle(key, 'email')} disabled={saving} />
            </div>
          )
        })}
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  )
}

function ToggleCell({ on, onChange, disabled }) {
  return (
    <div className="flex justify-center">
      <button
        onClick={onChange}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
          on ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        aria-pressed={on}
      >
        <span
          aria-hidden="true"
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-[22px]' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// QUIET HOURS TAB
// ────────────────────────────────────────────────────────────
function QuietHoursTab() {
  const [qh, setQh] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    notificationPreferencesApi.get().then(({ data }) => {
      setQh(data?.data?.quietHours || {
        enabled: false, from: '22:00', to: '07:00', timezone: 'Europe/Rome', bypassForUrgent: true
      })
    })
  }, [])

  const save = async (patch) => {
    setQh((p) => ({ ...p, ...patch }))
    setSaving(true)
    try {
      await notificationPreferencesApi.patch({ quietHours: patch })
    } finally {
      setSaving(false)
    }
  }

  if (!qh) return <p className="text-gray-500">Caricamento...</p>

  return (
    <div className="space-y-6 max-w-lg">
      <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
        <div>
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <BellOff className="h-5 w-5 text-gray-500" />
            Modalità non disturbare
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Non ricevere push durante una fascia oraria specifica
          </p>
        </div>
        <ToggleCell on={qh.enabled} onChange={() => save({ enabled: !qh.enabled })} disabled={saving} />
      </div>

      {qh.enabled && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Dalle</span>
              <input
                type="time"
                value={qh.from}
                onChange={(e) => save({ from: e.target.value })}
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Alle</span>
              <input
                type="time"
                value={qh.to}
                onChange={(e) => save({ to: e.target.value })}
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              />
            </label>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={qh.bypassForUrgent}
              onChange={() => save({ bypassForUrgent: !qh.bypassForUrgent })}
              className="mt-1"
            />
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Ricevi comunque le segnalazioni urgenti
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Le segnalazioni critiche superano l'orario di non disturbo
              </p>
            </div>
          </label>

          <p className="text-sm text-gray-600 dark:text-gray-400 italic">
            Sarai disturbato solo per emergenze tra le {qh.from} e le {qh.to} ({qh.timezone}).
          </p>
        </>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// DEVICES TAB
// ────────────────────────────────────────────────────────────
function DevicesTab() {
  const { isSupported, permission, isSubscribed, busy, error, subscribe, unsubscribe, sendTest } = usePushNotifications()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await pushApi.listDevices()
      setDevices(data?.data || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load, isSubscribed])

  const revoke = async (endpoint) => {
    if (!window.confirm('Vuoi revocare questo dispositivo?')) return
    await pushApi.unsubscribe(endpoint)
    load()
  }

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
        <h3 className="font-medium text-gray-900 dark:text-white">
          Questo dispositivo
        </h3>
        {!isSupported && (
          <p className="text-sm text-amber-600 mt-2">
            ⚠️ Browser non supportato. Aggiorna o usa Chrome/Firefox/Safari 16.4+.
          </p>
        )}
        {isSupported && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {permission === 'denied'
                ? <>❌ Notifiche bloccate dal browser. Vai nelle impostazioni del browser per riabilitarle.</>
                : isSubscribed
                  ? <>✅ Notifiche attive. Riceverai notifiche push anche con l'app chiusa.</>
                  : permission === 'granted'
                    ? <>Notifiche disattivate. Premi il pulsante qui sotto per ricevere notifiche push.</>
                    : <>Notifiche non ancora abilitate. Premi il pulsante qui sotto.</>
              }
            </p>
            <div className="flex gap-2 mt-3 flex-wrap">
              {!isSubscribed && (
                <button
                  onClick={subscribe}
                  disabled={busy}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {busy ? 'Attivazione...' : '🔔 Abilita notifiche'}
                </button>
              )}
              {isSubscribed && (
                <>
                  <button
                    onClick={sendTest}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Invia notifica di test
                  </button>
                  <button
                    onClick={unsubscribe}
                    disabled={busy}
                    className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50"
                  >
                    Disabilita notifiche
                  </button>
                </>
              )}
            </div>
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          </>
        )}
      </div>

      <div>
        <h3 className="font-medium text-gray-900 dark:text-white mb-3">
          Dispositivi registrati ({devices.length})
        </h3>
        {loading && <p className="text-sm text-gray-500">Caricamento...</p>}
        {!loading && devices.length === 0 && (
          <p className="text-sm text-gray-500">Nessun dispositivo registrato</p>
        )}
        <ul className="space-y-2">
          {devices.map((d) => {
            const Icon = ['ios-pwa', 'android-pwa', 'android-web'].includes(d.platform) ? Smartphone : Monitor
            return (
              <li
                key={d.id}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Icon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {d.browser} su {d.platform.replace('-', ' ')}
                      {d.disabled && <span className="ml-2 text-xs text-red-500">🔕 disabilitato</span>}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Ultimo uso: {new Date(d.lastUsedAt).toLocaleString('it-IT')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => revoke(d.endpoint)}
                  className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded"
                  aria-label="Revoca"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
